import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  FileText,
  ExternalLink,
  Search,
  LayoutTemplate
} from 'lucide-react';

const AdminPages = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPage, setNewPage] = useState({
    title: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    published: false
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/cms/pages`);
      setPages(res.data || []);
    } catch (err) {
      console.error('Error loading pages:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
      setError(errorMsg);
      toast.error(`Fehler beim Laden der Seiten: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setNewPage({
      ...newPage,
      title,
      slug: generateSlug(title),
      meta_title: title
    });
  };

  const handleCreatePage = async () => {
    if (!newPage.title || !newPage.slug) {
      toast.error('Bitte Titel und Slug angeben');
      return;
    }

    try {
      const res = await axios.post(`${API}/cms/pages`, newPage, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      toast.success('Seite erstellt');
      setShowCreateDialog(false);
      setNewPage({ title: '', slug: '', meta_title: '', meta_description: '', published: false });
      // Navigate to page editor
      navigate(`/admin/seiten/${res.data.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen der Seite');
    }
  };

  const handleDeletePage = async (slug) => {
    if (!window.confirm('Seite wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/cms/pages/${slug}`, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      toast.success('Seite gelöscht');
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      await axios.put(`${API}/cms/pages/${page.slug}`, 
        { published: !page.published },
        { headers: { Authorization: `Bearer ${admin.token}` }}
      );
      toast.success(page.published ? 'Seite deaktiviert' : 'Seite veröffentlicht');
      fetchPages();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const filteredPages = pages.filter(page => 
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-slate-900 mb-2">Fehler beim Laden</h3>
        <p className="text-slate-500 mb-4">{error}</p>
        <Button onClick={fetchPages} variant="outline">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pages">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Seiten verwalten</h1>
          <p className="text-slate-500">Erstellen und bearbeiten Sie Ihre Website-Seiten</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-page-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neue Seite
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Neue Seite erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Seitentitel *</Label>
                <Input
                  id="title"
                  value={newPage.title}
                  onChange={handleTitleChange}
                  placeholder="z.B. Über uns"
                  data-testid="page-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL-Pfad (Slug) *</Label>
                <div className="flex items-center">
                  <span className="text-sm text-slate-500 mr-2">/</span>
                  <Input
                    id="slug"
                    value={newPage.slug}
                    onChange={(e) => setNewPage({...newPage, slug: e.target.value})}
                    placeholder="ueber-uns"
                    data-testid="page-slug-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta-Titel (SEO)</Label>
                <Input
                  id="meta_title"
                  value={newPage.meta_title}
                  onChange={(e) => setNewPage({...newPage, meta_title: e.target.value})}
                  placeholder="Titel für Google"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta-Beschreibung (SEO)</Label>
                <Textarea
                  id="meta_description"
                  value={newPage.meta_description}
                  onChange={(e) => setNewPage({...newPage, meta_description: e.target.value})}
                  placeholder="Kurze Beschreibung für Suchmaschinen"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Abbrechen</Button>
              </DialogClose>
              <Button onClick={handleCreatePage} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-create-page">
                Erstellen & Bearbeiten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          placeholder="Seiten durchsuchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-pages"
        />
      </div>

      {/* Pages List */}
      {filteredPages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <LayoutTemplate className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchTerm ? 'Keine Seiten gefunden' : 'Noch keine Seiten erstellt'}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchTerm ? 'Versuche einen anderen Suchbegriff' : 'Erstelle deine erste Seite mit dem Page Builder'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowCreateDialog(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Erste Seite erstellen
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredPages.map((page) => (
            <Card key={page.id} className="hover:shadow-md transition-shadow" data-testid={`page-card-${page.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${page.published ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <FileText className={`w-5 h-5 ${page.published ? 'text-green-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{page.title}</h3>
                      <p className="text-sm text-slate-500">/{page.slug}</p>
                    </div>
                    {page.is_system_page && (
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                        System
                      </span>
                    )}
                    <span className={`px-2 py-1 text-xs rounded-full ${page.published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {page.published ? 'Veröffentlicht' : 'Entwurf'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {page.published && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/seite/${page.slug}`, '_blank')}
                        title="Seite anzeigen"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTogglePublish(page)}
                      title={page.published ? 'Deaktivieren' : 'Veröffentlichen'}
                    >
                      {page.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/seiten/${page.slug}`)}
                      title="Bearbeiten"
                      data-testid={`edit-page-${page.slug}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {!page.is_system_page && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePage(page.slug)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Löschen"
                        data-testid={`delete-page-${page.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPages;
